import { Injectable } from "@angular/core";
import { v4 as uuidv4 } from 'uuid';
import { CustomerdatabaseService } from "../services/customerdatabase.service";
import { environment } from "src/environments/environment";
import { EventLog, IErrorLoggerPayload, INavigationLoggerPayload, INetworkLoggerPayload, IUiLoggerPayload, EventType } from "./logging.model";
import { HttpClient } from "@angular/common/http";
import { AuthService } from "../services/auth.service";

@Injectable({ providedIn: 'root' })
export class LoggingService {
  private _eventLogs: EventLog[] = [];
  private _backupEventLogs: EventLog[] = []; //only used for creating error reports, as some logs could be deleted when limit is reached.
  private readonly _sessionId: string;
  private readonly _eventLogsLimit: number = 50;

  constructor(
    private customerDatabaseService: CustomerdatabaseService,
    private http: HttpClient,
    private authService: AuthService
  ) {
    this._sessionId = uuidv4();
  }

  getSessionId(): string {
    return this._sessionId;
  }

  private createMainPayload(eventType: EventType, context: string) {
    return {
      timestamp: new Date(),
      eventType: eventType,
      userId: this.customerDatabaseService.returnUserId() || '',
      accountId: this.customerDatabaseService.getAccountId() || '',
      sessionId: this.getSessionId() || '',
      payload: {
        context: context,
        version: environment.appVersion,
        environment: environment.environment,
        url: window.location.href,
        device: {
          userAgent: navigator.userAgent,
          screenResolution: `${window.screen.width}x${window.screen.height}`,
          windowSize: `${window.innerWidth}x${window.innerHeight}`,
          language: navigator.language,
          connection: (navigator as any).connection?.effectiveType || 'unknown',
        }
      }
    }
  }

  private addLog(log: EventLog) {
    if (this._eventLogs.length >= this._eventLogsLimit) {
      //reset the logs, send to cloud function to log it for big query later.
      this.logToServer();
      this._backupEventLogs = this._eventLogs.slice(); //keep a backup of previous logs for error reporting
      this._eventLogs = []
    }
    this._eventLogs.push(log);
  }

  logError(message: IErrorLoggerPayload) {
    const initialPayload = this.createMainPayload('ERROR', message.context);
    //check if the error comes from the logUserBehaviour endpoint, if so, skip logging to avoid infinite loops.
    if (message.message.includes('cloudfunctions.net/logUserBehaviour')) {
      return;
    }

    const payload = {
      ...initialPayload,
      error: {
        ...message
      }
    };
    //to make sure there is no infinite loop, we'll check the logs n see if there is already a log for the last entry.
    //if so, just skip.
    const recentLog = this._eventLogs[this._eventLogs.length - 1];
    if (recentLog?.eventType === 'ERROR' && recentLog?.error?.message === message.message) {
      return;
    }
    this.addLog(payload);
    this.logToServer();
  }

  logUi(element: IUiLoggerPayload) {
    const initialPayload = this.createMainPayload("UI", element.context || 'UI Interaction');
    const payload = {
      ...initialPayload,
      element: {
        context: element.context,
        elementPath: element.element ? this.getDomPath(element.element) : undefined,
        tag: element.tag,
        text: element.text,
        id: element.id,
        classes: element.classes,
      }
    }
    this.addLog(payload);
  }

  logNavigation(message: INavigationLoggerPayload) {
    const initialPayload = this.createMainPayload('NAVIGATION', 'Navigation');
    const payload = {
      ...initialPayload,
      navigation: {
        from: message.from,
        to: message.to,
        duration: message.duration
      }
    }
    this.addLog(payload);
  }

  logNetwork(message: INetworkLoggerPayload) {
    const initialPayload = this.createMainPayload('NETWORK', 'Network');
    const payload = {
      ...initialPayload,
      network: {
        method: message.method,
        url: message.url,
        status: message.status,
        duration: message.duration,
      }
    }
    this.addLog(payload);
  }

  /**
   * only used when the user leaves the page or unloads the window.
   */
  logLastLogs() {
    const initialPayload = this.createMainPayload('SYSTEM', 'Session End');
    this.addLog(initialPayload);
    this.logToServer();
    //reset the logs. 
    this._eventLogs = [];
  }


  /**
* Generates a CSS-like path: "div.container > ul > li:nth-child(2) > button"
* to know which element was interacted with.
*/
  private getDomPath(el: HTMLElement): string {
    const stack = [];
    let currentEl: HTMLElement | null = el;

    while (currentEl && currentEl.tagName !== 'BODY' && stack.length < 5) {
      let selector = currentEl.tagName.toLowerCase();

      // Add specific classes (optional, filter out Ionic junk)
      const validClasses = Array.from(currentEl.classList)
        .filter(c => !c.startsWith('ng-') && !c.startsWith('ion-'));

      if (validClasses.length > 0) {
        selector += `.${validClasses.join('.')}`;
      } else {
        // If no classes, use nth-child to be specific
        // e.g., "div:nth-child(3)"
        const siblingIndex = this.getSiblingIndex(currentEl);
        if (siblingIndex > 1) {
          selector += `:nth-child(${siblingIndex})`;
        }
      }

      stack.unshift(selector);
      currentEl = currentEl.parentElement;
    }

    return stack.join(' > ');
  }

  private getSiblingIndex(node: HTMLElement): number {
    let i = 1;
    while ((node = node.previousElementSibling as HTMLElement)) {
      i++;
    }
    return i;
  }

  private async logToServer() {
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
      return;
    }

    let logs;
    if (this._eventLogs[this._eventLogs.length - 1]?.eventType === 'ERROR') {
      logs = this._backupEventLogs.length > 0 ? this._backupEventLogs.concat(this._eventLogs) : this._eventLogs;
    } else {
      logs = this._eventLogs;
    }

    try {
      const idToken = await this.authService.getIdToken();
      if (!idToken) {
        return;
      }

      const payload = {
        userId: this.customerDatabaseService.returnUserId() || '',
        sessionId: this.getSessionId() || '',
        accountId: this.customerDatabaseService.getAccountId() || '',
        logs: logs
      };
      const header = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${idToken}`
      };
      this.http.post(environment.loggingURL, payload, { headers: header }).toPromise().catch(() => {});
    } catch {
    }
  }

}